import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Profile.css';

/* ═══════════════════════════════════════════════════════
   SVG Icons (No emojis — clean line art)
═══════════════════════════════════════════════════════ */
const base = (s, w) => ({
  width: s, height: s, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: w,
  strokeLinecap: 'round', strokeLinejoin: 'round',
});

const IcUser = ({ size = 20, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IcMail = ({ size = 18, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 6-10 7L2 6" />
  </svg>
);

const IcEdit = ({ size = 16, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IcBox = ({ size = 18, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <path d="M12 3 20 7.5v9L12 21 4 16.5v-9Z" />
    <path d="M4 7.5 12 12l8-4.5M12 12v9" />
  </svg>
);

const IcHeart = ({ size = 18, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <path d="M20.8 5.6a5.2 5.2 0 0 0-7.4 0L12 7l-1.4-1.4a5.2 5.2 0 1 0-7.4 7.4L12 21l8.8-8a5.2 5.2 0 0 0 0-7.4Z" />
  </svg>
);

const IcCart = ({ size = 18, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <path d="M3 4h2.2l2.2 11.2a1.8 1.8 0 0 0 1.77 1.42h8.66a1.8 1.8 0 0 0 1.76-1.4L21 8H6" />
    <circle cx="9.5" cy="20" r="1.3" />
    <circle cx="17.5" cy="20" r="1.3" />
  </svg>
);

const IcSettings = ({ size = 18, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);

const IcLogout = ({ size = 18, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const IcCheck = ({ size = 16, sw = 2.2 }) => (
  <svg {...base(size, sw)}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IcClose = ({ size = 16, sw = 2 }) => (
  <svg {...base(size, sw)}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IcAlert = ({ size = 16, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/* ═══════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════ */
const Profile = () => {
  const { user, loading, logout, setUser } = useAuth();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
  });

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div className="pr-page">
        <div className="pr-loading">
          <div className="pr-spinner" />
          <p>Loading your profile…</p>
        </div>
      </div>
    );
  }

  /* ─── Not logged in ─── */
  if (!user) {
    return (
      <div className="pr-page">
        <div className="pr-card pr-card-center">
          <div className="pr-icon-large">
            <IcUser size={48} sw={1.5} />
          </div>
          <h2 className="pr-empty-title">Not Logged In</h2>
          <p className="pr-empty-text">Please sign in to view your profile.</p>
          <Link to="/login" className="pr-btn pr-btn-primary">Sign In</Link>
        </div>
      </div>
    );
  }

  /* ─── Save profile ─── */
  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const res = await api.patch('/accounts/me/', {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
      });

      // Update user in context + localStorage
      const updatedUser = { ...user, ...res.data };
      if (setUser) setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setSuccess('Profile updated successfully');
      setEditing(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
    });
    setEditing(false);
    setError('');
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    setLoggingOut(false);
    navigate('/');
  };

  const initials = (user.first_name?.[0] || user.username?.[0] || 'U').toUpperCase();
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;

  return (
    <div className="pr-page">
      <div className="pr-card">

        {/* ═══════ SUCCESS / ERROR ═══════ */}
        {success && (
          <div className="pr-msg pr-msg-success">
            <IcCheck size={16} />
            {success}
          </div>
        )}
        {error && (
          <div className="pr-msg pr-msg-error">
            <IcAlert size={16} />
            {error}
          </div>
        )}

        {/* ═══════ HEADER ═══════ */}
        <div className="pr-header">
          <div className="pr-avatar">
            <span>{initials}</span>
          </div>
          <div className="pr-header-info">
            <h1 className="pr-name">{fullName}</h1>
            {user.email && (
              <p className="pr-email">
                <IcMail size={15} />
                {user.email}
              </p>
            )}
            <p className="pr-username">@{user.username}</p>
          </div>
          {!editing && (
            <button
              className="pr-btn pr-btn-edit"
              onClick={() => setEditing(true)}
              aria-label="Edit profile"
            >
              <IcEdit size={15} />
              Edit
            </button>
          )}
        </div>

        <div className="pr-divider" />

        {/* ═══════ EDIT FORM OR DETAILS ═══════ */}
        {editing ? (
          <div className="pr-section">
            <h3 className="pr-section-title">Edit Profile</h3>

            <div className="pr-field">
              <label className="pr-label">First Name</label>
              <input
                type="text"
                className="pr-input"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                placeholder="Enter first name"
              />
            </div>

            <div className="pr-field">
              <label className="pr-label">Last Name</label>
              <input
                type="text"
                className="pr-input"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                placeholder="Enter last name"
              />
            </div>

            <div className="pr-field">
              <label className="pr-label">Email</label>
              <input
                type="email"
                className="pr-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Enter email"
              />
            </div>

            <div className="pr-field">
              <label className="pr-label">Username</label>
              <input
                type="text"
                className="pr-input pr-input-disabled"
                value={user.username}
                disabled
              />
              <span className="pr-hint">Username cannot be changed</span>
            </div>

            <div className="pr-form-actions">
              <button
                className="pr-btn pr-btn-ghost"
                onClick={handleCancel}
                disabled={saving}
              >
                <IcClose size={15} />
                Cancel
              </button>
              <button
                className="pr-btn pr-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                <IcCheck size={15} />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="pr-section">
            <h3 className="pr-section-title">Account Details</h3>

            <div className="pr-detail-row">
              <span className="pr-detail-label">User ID</span>
              <span className="pr-detail-value">#{user.id}</span>
            </div>
            <div className="pr-detail-row">
              <span className="pr-detail-label">Username</span>
              <span className="pr-detail-value">{user.username}</span>
            </div>
            <div className="pr-detail-row">
              <span className="pr-detail-label">Full Name</span>
              <span className="pr-detail-value">{fullName}</span>
            </div>
            {user.email && (
              <div className="pr-detail-row">
                <span className="pr-detail-label">Email</span>
                <span className="pr-detail-value">{user.email}</span>
              </div>
            )}
          </div>
        )}

        <div className="pr-divider" />

        {/* ═══════ QUICK LINKS ═══════ */}
        <div className="pr-section">
          <h3 className="pr-section-title">Quick Links</h3>
          <div className="pr-links-grid">
            <Link to="/orders" className="pr-link">
              <span className="pr-link-icon"><IcBox size={20} /></span>
              <span className="pr-link-text">My Orders</span>
            </Link>
            <Link to="/wishlist" className="pr-link">
              <span className="pr-link-icon"><IcHeart size={20} /></span>
              <span className="pr-link-text">Wishlist</span>
            </Link>
            <Link to="/cart" className="pr-link">
              <span className="pr-link-icon"><IcCart size={20} /></span>
              <span className="pr-link-text">Cart</span>
            </Link>
            <Link to="/settings" className="pr-link">
              <span className="pr-link-icon"><IcSettings size={20} /></span>
              <span className="pr-link-text">Settings</span>
            </Link>
          </div>
        </div>

        <div className="pr-divider" />

        {/* ═══════ LOGOUT ═══════ */}
        <button
          className="pr-btn pr-btn-danger"
          onClick={handleLogout}
          disabled={loggingOut}
        >
          <IcLogout size={17} />
          {loggingOut ? 'Logging out…' : 'Logout'}
        </button>

      </div>
    </div>
  );
};

export default Profile;